// eslint-disable-next-line import-x/no-extraneous-dependencies
import { ZonedDateTime, parseAbsolute } from '@internationalized/date';
import Highcharts, {
  Options as HighchartsOptions,
  Point,
  SeriesLineOptions,
  SeriesOptions,
  XAxisOptions,
  YAxisOptions,
} from 'highcharts';
import HighchartsBoost from 'highcharts/modules/boost';
import HighchartsReact from 'highcharts-react-official';
import { TFunction } from 'i18next';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import ReactResizeDetector from 'react-resize-detector';

import { DEFAULT_PERCENTAGE_LOCALE_OPTIONS, chartsConfig, staticChartOptions } from './config';
import { ChartsTypes, IntradaySupportedSeries } from './types';
import {
  TOOLTIP_DISTANCE_FROM_POINT,
  TOOLTIP_DISTANCE_FROM_WINDOW_BORDER,
  chartTooltipFormatter,
  chartXAxisLabelFormatter,
} from './utils';
import { useAuth } from '../../app/AuthorizationProvider';
import { ChartData, RecordBooleanType } from '../../types';
import { getZonedDateTimeOffset } from '../../utils/date';
import { chartYAxisLabelFormatter } from '../ForecastingStudio/utils';

import type { DateFormatterOptions } from 'react-aria';

HighchartsBoost(Highcharts);

export const modifyLastValueOfSeries = (data: ChartData[]) => {
  if (!data.length) {
    return data;
  }

  return [
    ...data.slice(0, data.length - 1),
    {
      /**
       * Last value is provided only to have last interval present with line, even thought
       * this ending point should not be neither hoverable, presented with marker or tooltip
       */
      hideTooltip: true,
      marker: {
        enabled: false,
        states: {
          hover: {
            enabled: false,
          },
        },
      },
      x: data[data.length - 1][0],
      y: data[data.length - 1][1],
    },
  ];
};

/* eslint-disable react/no-unused-prop-types */

export type IntradayChartProps = {
  data?: IntradaySupportedSeries;
  dataAsOf?: ZonedDateTime;
  startDateTime?: ZonedDateTime;
  endDateTime?: ZonedDateTime;
  chartType: keyof ChartsTypes;
  seriesDataVisibility: RecordBooleanType;
  timezone?: string;
};

export const generateChartConfig: (
  props: IntradayChartProps,
  timezone: string,
  locale: string,
  t: TFunction
) => HighchartsOptions = (
  { data, dataAsOf, endDateTime, chartType, seriesDataVisibility, startDateTime },
  timezone,
  locale,
  t
) => {
  const { isPercentage = false, seriesConfig, seriesTrendLegendRelatedSeries, yAxisConfig } = chartsConfig[chartType];
  const dateFormatterOptions: DateFormatterOptions = {
    hour: 'numeric',
    minute: 'numeric',
    timeZone: timezone,
  };

  return {
    ...staticChartOptions,
    series: [
      ...seriesConfig.map(
        ({ color, isDashed, nameKey }) =>
          ({
            ...(staticChartOptions.series as SeriesOptions[])[0],
            color,
            dashStyle: isDashed ? 'ShortDash' : 'Solid',
            data: modifyLastValueOfSeries(data?.[nameKey as keyof IntradaySupportedSeries] ?? []),
            id: nameKey,
            marker: {
              ...(staticChartOptions.series as SeriesLineOptions[])[0].marker,
              states: {
                ...(staticChartOptions.series as SeriesLineOptions[])[0].marker?.states,
                hover: {
                  ...(staticChartOptions.series as SeriesLineOptions[])[0].marker?.states?.hover,
                  lineColor: color,
                },
              },
            },
            name: t(`intraday.charts.legendItems.${nameKey}`),
            step: 'left' as SeriesLineOptions['step'],
            visible:
              nameKey === 'trend' && seriesTrendLegendRelatedSeries
                ? seriesDataVisibility[seriesTrendLegendRelatedSeries]
                : seriesDataVisibility[nameKey],
          } as SeriesLineOptions)
      ),
    ],
    time: {
      getTimezoneOffset: (timestamp: number) =>
        getZonedDateTimeOffset(parseAbsolute(new Date(timestamp).toISOString(), timezone)),
      useUTC: true,
    },
    tooltip: {
      ...staticChartOptions.tooltip,
      formatter: chartTooltipFormatter(locale, timezone, isPercentage ? DEFAULT_PERCENTAGE_LOCALE_OPTIONS : undefined),
      positioner(labelWidth, labelHeight, point) {
        // @ts-expect-error not properly resolved type
        const { chart } = this; // `this` is of type `TooltipOptions`

        const hoverPoints = (chart.hoverPoints ?? []) as Point[];
        const maxY = Math.max(...hoverPoints.map(({ y }) => y ?? 0));
        const pointWithMaxY = hoverPoints.find(({ y }) => y === maxY);
        // @ts-expect-error plotY is part of object even thought not defined in type
        const plotY = pointWithMaxY?.plotY;
        const { left, top } = chart.pointer.chartPosition;

        let x = chart.plotLeft + left + point.plotX - labelWidth / 2;
        let y = top + plotY - labelHeight;

        if (x < TOOLTIP_DISTANCE_FROM_WINDOW_BORDER) {
          x = TOOLTIP_DISTANCE_FROM_WINDOW_BORDER;
        } else if (window.innerWidth <= x + labelWidth + TOOLTIP_DISTANCE_FROM_WINDOW_BORDER) {
          x = window.innerWidth - labelWidth - TOOLTIP_DISTANCE_FROM_WINDOW_BORDER;
        }

        // adjustments needed when having charts rendered under each other and scrolling through the viewport...
        if (y < TOOLTIP_DISTANCE_FROM_POINT) {
          y = TOOLTIP_DISTANCE_FROM_POINT;
        } else if (y + labelHeight + TOOLTIP_DISTANCE_FROM_POINT > window.innerHeight) {
          y = window.innerHeight - labelHeight - TOOLTIP_DISTANCE_FROM_POINT;
        }

        return {
          x,
          y,
        };
      },
    },
    xAxis: {
      ...staticChartOptions.xAxis,
      min: startDateTime?.toDate().getTime(),
      max: endDateTime?.toDate().getTime(),
      labels: {
        ...(staticChartOptions.xAxis as XAxisOptions).labels,
        formatter: chartXAxisLabelFormatter(locale, dateFormatterOptions),
        useHTML: true,
      },
      plotLines: [
        {
          ...(staticChartOptions.xAxis as XAxisOptions).plotLines?.[0],
          value: dataAsOf?.toDate()?.getTime(),
        },
      ],
    },
    yAxis: {
      ...staticChartOptions.yAxis,
      minTickInterval: isPercentage ? undefined : 10,
      allowDecimals: isPercentage,
      labels: {
        ...(staticChartOptions.yAxis as YAxisOptions).labels,
        formatter: chartYAxisLabelFormatter(locale, isPercentage ? DEFAULT_PERCENTAGE_LOCALE_OPTIONS : undefined),
      },
      ...yAxisConfig,
    },
  };
};

export const IntradayChart: React.FC<IntradayChartProps> = (props) => {
  const { locale, timezone: userTimezone } = useAuth();
  const chartRef = useRef<HighchartsReact>(null);
  const { t } = useTranslation();

  const { timezone } = props;

  const onResizeHandler = () => {
    if (chartRef && chartRef.current) {
      chartRef.current.chart.reflow();
    }
  };

  return (
    <ReactResizeDetector handleWidth handleHeight onResize={onResizeHandler}>
      <HighchartsReact
        containerProps={{ className: 'h-full', 'data-test-id': 'chart:container' }}
        highcharts={Highcharts}
        options={generateChartConfig(props, timezone ?? userTimezone, locale, t)}
        ref={chartRef}
        updateArgs={[true, true, false]}
      />
    </ReactResizeDetector>
  );
};
